<template>
  <BaseModal v-model="modelValue" title="FCP Details" @hide="handleCancel">
    <div class="mb-3">
      <label class="form-label">
        FCP Start Date <span class="text-danger">*</span>
      </label>
      <DatePicker
        v-model="fcpStartDate"
        :format="DATE_FORMAT"
        value-type="format"
        :clearable="false"
        class="w-100"
      />
      <div v-if="errors.fcpStartDate" class="text-danger small mt-1">
        {{ errors.fcpStartDate }}
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label">
        Account Type <span class="text-danger">*</span>
      </label>
      <BaseSelect
        v-model="accountTypeValue"
        :options="BDX_ACCOUNT_TYPES"
        size="sm"
      >
        <template v-slot:first>
          <b-form-select-option :value="undefined" disabled>
            Select Account Type
          </b-form-select-option>
        </template>
      </BaseSelect>
      <div v-if="errors.accountType" class="text-danger small mt-1">
        {{ errors.accountType }}
      </div>
    </div>

    <div class="mb-3">
      <label class="form-label">
        Account Name <span class="text-danger">*</span>
      </label>
      <BDXEditFormAccountName v-model="accountNameValue" :umr="umr" :show-label="false" />
      <div v-if="errors.accountName" class="text-danger small mt-1">
        {{ errors.accountName }}
      </div>
    </div>

    <div class="d-flex justify-content-end gap-2">
      <BaseButton variant="secondary" @click="handleCancel">Cancel</BaseButton>
      <BaseButton
        :loading="isSaving"
        :disabled="!isFormValid"
        @click="handleSave"
      >
        Save
      </BaseButton>
    </div>
  </BaseModal>
</template>

<script>
import { defineComponent, computed, ref, watch } from '@vue/composition-api'
import DatePicker from 'vue2-datepicker'
import { DATE_FORMAT, BDX_ACCOUNT_TYPES } from '@constants'
import { BaseModal, BaseButton, BaseSelect } from '@components/base'
import BDXEditFormAccountName from '../bdx-edit-form/BDXEditFormAccountName.vue'

export default defineComponent({
  name: 'BDXManagementTableFCPToggleStartDateModal',
  components: {
    DatePicker,
    BaseModal,
    BaseButton,
    BaseSelect,
    BDXEditFormAccountName
  },
  props: {
    value: Boolean,
    id: {
      type: Number,
      required: true
    },
    date: String,
    accountType: String,
    accountName: String,
    umr: String,
    isTurningOn: {
      type: Boolean,
      default: false
    }
  },
  emits: ['input', 'save', 'cancel'],
  setup(props, { emit }) {
    const isSaving = ref(false)
    const fcpStartDate = ref(props.date)
    const accountTypeValue = ref(props.accountType)
    const accountNameValue = ref(props.accountName)
    const errors = ref({
      fcpStartDate: '',
      accountType: '',
      accountName: ''
    })

    const modelValue = computed({
      get: () => props.value,
      set: (newValue) => emit('input', newValue)
    })

    const isFormValid = computed(() => {
      return (
        !!fcpStartDate.value &&
        !!accountTypeValue.value &&
        !!accountNameValue.value
      )
    })

    const validateForm = () => {
      errors.value = {
        fcpStartDate: '',
        accountType: '',
        accountName: ''
      }

      let isValid = true

      if (!fcpStartDate.value) {
        errors.value.fcpStartDate = 'FCP start date is required'
        isValid = false
      }

      if (!accountTypeValue.value) {
        errors.value.accountType = 'Account type is required'
        isValid = false
      }

      if (!accountNameValue.value) {
        errors.value.accountName = 'Account name is required'
        isValid = false
      }

      return isValid
    }

    const handleSave = () => {
      if (!validateForm()) {
        return
      }

      isSaving.value = true
      emit('save', {
        date: fcpStartDate.value,
        accountType: accountTypeValue.value,
        accountName: accountNameValue.value
      })
    }

    const handleCancel = () => {
      emit('cancel')
      modelValue.value = false
    }

    watch(modelValue, (newVal) => {
      if (newVal) {
        fcpStartDate.value = props.date
        accountTypeValue.value = props.accountType
        accountNameValue.value = props.accountName
        errors.value = {
          fcpStartDate: '',
          accountType: '',
          accountName: ''
        }

        if (props.isTurningOn && !fcpStartDate.value) {
          const today = new Date()
          fcpStartDate.value = today.toISOString().split('T')[0]
        }
      }
    })

    return {
      DATE_FORMAT,
      BDX_ACCOUNT_TYPES,
      modelValue,
      fcpStartDate,
      accountTypeValue,
      accountNameValue,
      errors,
      isSaving,
      isFormValid,
      handleSave,
      handleCancel
    }
  }
})
</script>

<style scoped>
.mx-datepicker {
  width: 100%;
}

.mx-datepicker >>> .mx-input-wrapper input {
  border-color: #c4c4c4;
  border-radius: 4px;
}
</style>
